/**
 * Hand-rolled RFC4180 CSV — parse and serialize. No dependency: Letterboxd
 * exports need nothing more than quoted fields, embedded commas/newlines, and
 * `""`-escaped quotes, and a dependency-free parser is easy to golden-fixture
 * test (see tests/letterboxd-csv.test.ts) rather than trust sight-unseen.
 *
 * Titles like `Thor: Love and Thunder` (colon, no comma) parse fine even
 * unquoted; the fixture also covers commas and embedded newlines inside
 * quoted fields, which unquoted parsing can't handle at all.
 */

/** Parses raw CSV text into rows of raw string cells. Strips a leading BOM. */
export function parseCsv(text: string): string[][] {
  const src = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;
  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";
  let inQuotes = false;
  let i = 0;
  const n = src.length;

  const endField = () => {
    row.push(field);
    field = "";
  };
  const endRow = () => {
    endField();
    rows.push(row);
    row = [];
  };

  while (i < n) {
    const ch = src[i];
    if (inQuotes) {
      if (ch === '"') {
        if (src[i + 1] === '"') {
          field += '"';
          i += 2;
          continue;
        }
        inQuotes = false;
        i++;
        continue;
      }
      field += ch;
      i++;
      continue;
    }
    if (ch === '"') {
      inQuotes = true;
      i++;
      continue;
    }
    if (ch === ",") {
      endField();
      i++;
      continue;
    }
    if (ch === "\r") {
      if (src[i + 1] === "\n") i++;
      endRow();
      i++;
      continue;
    }
    if (ch === "\n") {
      endRow();
      i++;
      continue;
    }
    field += ch;
    i++;
  }
  if (field.length > 0 || row.length > 0) endRow();

  // A trailing blank line in the file (not a trailing newline on the last
  // data row) parses as one all-empty row; drop it as a formatting artifact.
  return rows.filter((r) => !(r.length === 1 && r[0] === ""));
}

/** Parses CSV text with a header row into plain objects keyed by header name. */
export function csvRecords(text: string): Record<string, string>[] {
  const rows = parseCsv(text);
  const header = rows[0];
  if (!header) return [];
  return rows.slice(1).map((row) => {
    const record: Record<string, string> = {};
    header.forEach((key, index) => {
      record[key] = row[index] ?? "";
    });
    return record;
  });
}

function csvField(value: string): string {
  return /[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

/** Serializes a header + rows back to RFC4180 text (CRLF line endings, quoted as needed). */
export function toCsv(header: string[], rows: string[][]): string {
  return `${[header, ...rows].map((row) => row.map(csvField).join(",")).join("\r\n")}\r\n`;
}
